import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ShieldCheck, Award, Building2, CheckCircle2, BadgeCheck, Kayak, Scale } from 'lucide-react';

const MarketPresence = () => {
  // --- STATE FOR CARD STACK ---
  const [activeIndex, setActiveIndex] = useState(0);

  const locations = [
    { city: "Arlington, Virginia", focus: "Corporate Headquarters & Construction Operations" },
    { city: "Washington, DC", focus: "Federal & Government Projects Division" },
    { city: "Maryland", focus: "Sustainable Development & Green Building Division" },
    { city: "Delaware", focus: "Investment Services & Corporate Development" },
    { city: "Florida", focus: "Investment & Consulting Services" },
  ];

  const certifications = [
    { title: "Virginia Contractor", subtitle: "Class A License", icon: <BadgeCheck /> },
    { title: "Delaware License", subtitle: "Business License", icon: <Scale /> },
    { title: "BBB Accredited", subtitle: "A+ Rating", icon: <Award /> },
    { title: "HomeAdvisor", subtitle: "Elite Service", icon: <CheckCircle2 /> },
    { title: "OSHA Certified", subtitle: "30-Hour Safety", icon: <Award /> },
    { title: "Fully Insured", subtitle: "Liability Insurance", icon: <ShieldCheck /> },
    { title: "Bonded Capacity", subtitle: "Performance Bond", icon: <Kayak /> },
  ];

  const memberships = [
    "Associated General Contractors of America (AGC)",
    "National Association of Home Builders (NAHB)",
    "U.S. Green Building Council (USGBC)",
    "Construction Management Association of America (CMAA)",
    "National Institute of Building Sciences (NIBS)",
  ];

  // Auto-Cycle Stack Logic
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % memberships.length);
    }, 3000); // Change every 3 seconds
    return () => clearInterval(interval);
  }, [memberships.length]);

  // General Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: "spring", stiffness: 50 } }
  };

  return (
    <section className="min-h-screen py-20 bg-[#fffcf5] relative overflow-hidden font-sans" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gray-100/50 skew-x-12 translate-x-20 pointer-events-none" />
      
      {/* Background Decor */}
      {/* <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-white to-slate-50 z-0"></div>
      <div className="absolute right-0 top-20 w-64 h-64 bg-blue-100/50 rounded-full blur-3xl z-0"></div>
      <div className="absolute left-0 bottom-20 w-80 h-80 bg-yellow-100/30 rounded-full blur-3xl z-0"></div> */}

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* --- HEADER --- */}
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6"
          >
            Market Presence <span className="text-slate-300 font-light">&</span> <span className="text-[#2a3890]">Authority</span>
            <motion.div 
              initial={{ width: 0 }}
              whileInView={{ width: 200 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-[#2a3890] mx-auto rounded-full mt-4"
              />
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-slate-600 max-w-2xl mx-auto text-lg"
          >
            Established operational hubs and verified credentials that demonstrate our commitment to excellence and compliance.
          </motion.p>
        </div>

        {/* --- 1. GEOGRAPHIC COVERAGE (Locations) --- */}
        <div className="mb-24">
          <div className="flex items-center gap-4 mb-10">
            <div className="h-10 w-1 bg-[#2a3890] rounded-full"></div>
            <h3 className="text-2xl font-bold text-slate-900">Geographic Coverage</h3>
          </div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {locations.map((loc, index) => (
              <motion.div 
                key={index} 
                variants={itemVariants}
                className="group relative bg-white p-4 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#2a3890] to-[#9a8456] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                <div className="flex items-start justify-between mb-4">
                   <div className="p-3 bg-slate-50 rounded-lg text-[#2a3890] group-hover:bg-[#2a3890] group-hover:text-white transition-colors">
                     <MapPin size={24} />
                   </div>
                   {/* <span className="text-xs font-bold text-slate-300">0{index + 1}</span> */}
                </div>
                <h4 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-[#2a3890] transition-colors">{loc.city}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{loc.focus}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* --- 2) COMBINED SECTION: Certifications & Memberships --- */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
          
          {/* LEFT: Certifications (Grid Layout) */}
          <div className="xl:col-span-8">
            <div className="flex items-center gap-4 mb-10">
              <div className="h-10 w-1 bg-[#9a8456] rounded-full"></div>
              <h3 className="text-2xl font-bold text-slate-900">Licenses & Certifications</h3>
            </div>
            
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {certifications.map((cert, index) => (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="flex flex-col items-center text-center p-2 bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-[#9a8456]/30 transition-all duration-300"
                >
                  <div className="mb-4 p-3 bg-slate-50 rounded-full text-[#9a8456]">
                    {React.cloneElement(cert.icon, { size: 28 })}
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm mb-1">{cert.title}</h4>
                  <p className="text-xs text-slate-500">{cert.subtitle}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT: Memberships (STACK ANIMATION - LIGHT THEME) */}
          <div className="xl:col-span-4 mt-20 h-full flex flex-col">
            <div className="flex items-center gap-4 mb-10">
               <div className="h-10 w-1 bg-[#2a3890] rounded-full"></div> 
               <h3 className="text-2xl font-bold text-slate-900">Industry Memberships</h3> 
            </div>

            {/* Stack Container */}
            <div className="relative w-full h-[400px] flex items-center justify-center">
               <AnimatePresence mode='popLayout'>
                 {memberships.map((member, index) => {
                   // Calculate offset for stacking effect
                   const isFront = index === activeIndex;
                   console.log(isFront);
                   const offset = (index - activeIndex + memberships.length) % memberships.length;
                   console.log(offset); 
                   // Only render top 3 cards for performance and visual clarity
                   if (offset > 2) return null;

                   return (
                     <motion.div
                       key={member}
                       layout
                       initial={{ opacity: 0, scale: 0.9, y: 20 }}
                       animate={{ 
                         opacity: offset === 0 ? 1 : 1 - (offset * 0.3),
                         scale: 1 - (offset * 0.05),
                         y: offset * 15,
                         zIndex: 10 - offset
                       }}
                       exit={{ opacity: 0, y: -20, scale: 0.95, transition: { duration: 0.3 } }}
                       transition={{ duration: 0.5, ease: "easeInOut" }}
                       className={`absolute top-0 w-full p-8 shadow-xl flex flex-col justify-between h-[320px] overflow-hidden border transition-colors duration-500
                         ${offset === 0 ? 'bg-[#fffcf5] border-[#2a3890]/20' : 'bg-slate-100 border-slate-200'}
                       `}
                       style={{ 
                         transformOrigin: "top center",
                       }}
                     >
                        {/* Background Image (Construction/Architecture) */}
                        <div className="absolute inset-0 z-0">
                           <img 
                             src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=1000&auto=format&fit=crop" 
                             alt="Construction"
                             className="w-full h-full object-cover opacity-[0.08]" 
                           />
                           <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-white/60"></div>
                           <div className="absolute top-0 right-0 w-1/3 h-full bg-gray-100/50 skew-x-12 translate-x-20 pointer-events-none" />
                        </div>

                        {/* Content */}
                        <div className="relative z-10">
                          <div className="flex justify-between items-start mb-6">
                            <div className={`p-3 rounded-xl ${offset === 0 ? 'bg-[#2a3890]/10' : 'bg-slate-200'}`}>
                              <Building2 className={`${offset === 0 ? 'text-[#2a3890]' : 'text-slate-400'}`} size={32} />
                            </div>
                          </div>
                          
                          <h4 className="text-xl md:text-2xl font-bold text-slate-800 leading-snug">
                            {member}
                          </h4>
                        </div>

                        <div className="relative z-10 mt-auto pt-6 border-t border-slate-100 flex items-center gap-2">
                           <CheckCircle2 size={16} className="text-[#9a8456]" />
                           <span className="text-xs font-bold text-[#9a8456] uppercase tracking-widest">Active Member</span>
                        </div>
                     </motion.div>
                   );
                 })}
               </AnimatePresence>
            </div>
            
            {/* Controls */}
            <div className="flex justify-center gap-2 mt-4">
              {memberships.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === activeIndex ? 'w-8 bg-[#2a3890]' : 'bg-slate-300'}`}
                />
              ))}
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default MarketPresence;